/**
 * Termal fiş (80mm) yazdırma yardımcıları.
 *
 * Trendyol GO / Uber Eats siparişi geldiğinde mutfağa otomatik fiş bastırmak
 * için kullanılır. Gizli bir <iframe> içine fiş HTML'i yazılır ve
 * `iframe.contentWindow.print()` çağrılır.
 *
 * ÖNEMLİ — "dialogsuz" otomatik baskı: Standart tarayıcı her print()'te bir
 * yazdırma penceresi açar. Penceresiz (gerçekten otomatik) baskı için Chrome'u
 * `--kiosk-printing` bayrağıyla açıp termal yazıcıyı VARSAYILAN yazıcı yapın;
 * o modda print() doğrudan varsayılan yazıcıya basar. Kod her iki durumda da
 * çalışır — kiosk modu yoksa kullanıcı tek "Yazdır"a basar.
 */

export interface ReceiptItem {
  name: string;
  price: number;
  quantity: number;
  note?: string;
}

export interface ReceiptOrder {
  id?: string;
  _id?: string;
  table: string;
  items: ReceiptItem[];
  totalPrice: number;
  note?: string;
  paymentMethod?: string;
  customerName?: string;
  orderCode?: string;
  isPickup?: boolean;
  appName?: string;
  discountAmount?: number;
  discountLabel?: string;
  createdAt?: string;
  source?: string;
}

export interface ReceiptOptions {
  /** Üst başlık (varsayılan markaya göre). */
  title?: string;
  subtitle?: string;
  /** Alt bilgi satırları (ör. restoran destek hattı). */
  footerLines?: string[];
}

const money = (n: number) =>
  (Number.isFinite(n) ? n : 0).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string)
  );

/** "Trendyol #1234" → "1234"; eşleşmezse table'ı olduğu gibi döndürür. */
function orderCodeFromTable(table: string): string {
  const m = table.match(/#\s*([\w-]+)/);
  return m ? m[1] : table;
}

/** Sipariş notunu ("Trendyol GO • müşteri notu") müşteri kısmına indirger. */
function customerNote(note?: string): string | undefined {
  if (!note) return undefined;
  const idx = note.indexOf("•");
  const tail = idx >= 0 ? note.slice(idx + 1).trim() : note.trim();
  // "Trendyol GO siparişi" gibi jenerik notları gösterme.
  if (!tail || /sipari[şs]i$/i.test(tail)) return undefined;
  return tail;
}

/** Tek bir siparişi 80mm termal fiş HTML'ine çevirir. */
export function buildReceiptHtml(order: ReceiptOrder, opts: ReceiptOptions = {}): string {
  const title = opts.title ?? "Trendyol GO";
  const subtitle = opts.subtitle ?? "Uber Eats";
  const code = order.orderCode || orderCodeFromTable(order.table);
  const date = order.createdAt ? new Date(order.createdAt) : new Date();
  const dateStr = date.toLocaleString("tr-TR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const note = customerNote(order.note);

  const rows = order.items
    .map((it) => {
      const lineTotal = money(it.price * it.quantity);
      const noteLine = it.note
        ? `<div class="opt">${esc(it.note)}</div>`
        : "";
      return `
        <tr>
          <td class="name">${esc(it.name)}${noteLine}</td>
          <td class="qty">${it.quantity}</td>
          <td class="amt">${lineTotal}</td>
        </tr>`;
    })
    .join("");

  const footer = (opts.footerLines ?? [])
    .filter(Boolean)
    .map((l) => `<div class="foot">${esc(l)}</div>`)
    .join("");

  return `<!doctype html>
<html lang="tr"><head><meta charset="utf-8">
<title>Fiş ${esc(code)}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    width: 80mm;
    padding: 4mm 3mm;
    font-family: "Menlo", "Consolas", monospace;
    font-size: 12px;
    line-height: 1.35;
    color: #000;
    -webkit-print-color-adjust: exact;
  }
  .center { text-align: center; }
  .brand { font-size: 18px; font-weight: 700; letter-spacing: .5px; }
  .sub { font-size: 12px; }
  .hr { border-top: 1px dashed #000; margin: 6px 0; }
  .hr2 { border-top: 2px solid #000; margin: 6px 0; }
  .code { font-size: 17px; font-weight: 700; text-align: center; }
  .meta { font-size: 12px; }
  .cust { font-size: 13px; font-weight: 700; margin: 2px 0; }
  .note { font-weight: 700; margin: 4px 0; }
  table { width: 100%; border-collapse: collapse; }
  th { font-size: 11px; text-align: left; border-bottom: 1px solid #000; padding-bottom: 2px; }
  th.qty, td.qty { text-align: center; width: 12mm; }
  th.amt, td.amt { text-align: right; width: 20mm; white-space: nowrap; }
  td { vertical-align: top; padding: 3px 0; }
  td.name { padding-right: 2mm; word-break: break-word; }
  .opt { font-size: 11px; font-weight: 400; padding-left: 4px; }
  .total { display: flex; justify-content: space-between; font-size: 15px; font-weight: 700; margin-top: 4px; }
  .pickup { text-align: center; font-size: 15px; font-weight: 700; border: 2px solid #000; padding: 3px; margin: 4px 0; }
  .disc { font-size: 12px; margin-top: 2px; }
  .pay { font-size: 13px; font-weight: 700; margin-top: 4px; }
  .foot { font-size: 10px; margin-top: 4px; word-break: break-word; }
</style></head>
<body>
  <div class="center sub">${esc(subtitle)}</div>
  <div class="center brand">${esc(title)}</div>
  <div class="hr2"></div>
  <div class="code">Sipariş Kodu: ${esc(code)}</div>
  ${order.isPickup ? `<div class="pickup">★ GEL-AL SİPARİŞİ ★</div>` : ""}
  <div class="hr2"></div>
  <div class="meta">${esc(dateStr)}${order.appName ? ` · ${esc(order.appName)}` : ""}</div>
  ${order.customerName ? `<div class="cust">${esc(order.customerName)}</div>` : ""}
  ${note ? `<div class="note">Sipariş Notu: ${esc(note)}</div>` : ""}
  <div class="hr"></div>
  <table>
    <thead><tr><th>Ürün</th><th class="qty">Adet</th><th class="amt">Tutar</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="hr"></div>
  <div class="total"><span>Toplam</span><span>${money(order.totalPrice)} ₺</span></div>
  ${order.discountAmount ? `<div class="disc">İndirim: ${order.discountLabel ? esc(order.discountLabel) + " " : ""}-${money(order.discountAmount)} ₺</div>` : ""}
  ${order.paymentMethod ? `<div class="pay">Ödeme: ${esc(order.paymentMethod)}</div>` : ""}
  ${footer ? `<div class="hr"></div>${footer}` : ""}
</body></html>`;
}

/** Verilen HTML'i gizli iframe içinde yazdırır. */
export function printHtml(html: string): void {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const cleanup = () => {
    // Baskı diyaloğu kapanınca iframe'i kaldır (gecikmeli — bazı tarayıcılarda
    // print() senkron döner ama render bitmemiş olabilir).
    setTimeout(() => iframe.remove(), 1000);
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) { iframe.remove(); return; }
  doc.open();
  doc.write(html);
  doc.close();

  const win = iframe.contentWindow;
  if (!win) { iframe.remove(); return; }
  // İçerik (font/layout) yerleşsin diye bir frame bekle.
  const fire = () => {
    try {
      win.focus();
      win.print();
    } finally {
      cleanup();
    }
  };
  if (doc.readyState === "complete") {
    setTimeout(fire, 50);
  } else {
    win.addEventListener("load", () => setTimeout(fire, 50), { once: true });
  }
}

/** Siparişi fiş olarak yazdırır. */
export function printOrderReceipt(order: ReceiptOrder, opts?: ReceiptOptions): void {
  printHtml(buildReceiptHtml(order, opts));
}

/** Yazıcı/şablon denemek için örnek fiş. */
export function printTestReceipt(opts?: ReceiptOptions): void {
  printOrderReceipt(
    {
      table: "Trendyol #TEST",
      orderCode: "005",
      createdAt: new Date().toISOString(),
      customerName: "Gözde Y.",
      appName: "Trendyol GO",
      isPickup: true,
      discountAmount: 50,
      discountLabel: "Hoş Geldin Kuponu",
      paymentMethod: "Kapıda Nakit",
      note: "Trendyol GO • Servis İstiyorum",
      items: [
        { name: "Bardakta White (özel belçika küp waffle parçaları, beyaz çikolata, muz, çilek, fındık)", price: 297, quantity: 1 },
        { name: "İstiyorum", price: 0, quantity: 1 },
      ],
      totalPrice: 297,
      source: "trendyolgo",
    },
    opts
  );
}
